import React from 'react';
import { useParams } from 'react-router-dom';
import ConfirmPolicies from '../steps/ConfirmPolicies';
import ConfirmSchedule from '../steps/ConfirmSchedule';
import PayFees from '../steps/PayFees';
import SignDocumentation from '../steps/SignDocumentation';
import Complete from '../steps/Complete';
import PendingEnrollmentPayment from './PendingEnrollmentPayment';

interface RegisterData {
  data?: {
    total?: number;
    paymentIntent?:
      | {
          id?: string;
          client_secret?: string;
        }
      | string;
    metadata?: {
      requiresPayment?: boolean;
      isBilledToOrganization?: boolean;
    };
    studentId?: number;
    studentRegistrationWindowId?: number;
    studentsSchedules?: Array<{
      currentlySchedulingId?: string;
    }>;
  };
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

interface StepRendererProps {
  step: number;
  setStep: (step: number) => void;
  registerData: RegisterData;
  cancelRegistration: {
    isLoading: boolean;
  };
  onConfirmSchedule: () => void;
  onCancelRegistration: () => void;
}

const StepRenderer: React.FC<StepRendererProps> = ({
  step,
  setStep,
  registerData,
  cancelRegistration,
  onConfirmSchedule,
  onCancelRegistration,
}) => {
  const params = useParams();

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <ConfirmPolicies
            onNextStep={() => setStep(1)}
            onCancel={onCancelRegistration}
            isCanceling={cancelRegistration.isLoading}
          />
        );
      case 1:
        return (
          <ConfirmSchedule
            onNextStep={onConfirmSchedule}
            onCancel={onCancelRegistration}
            isCanceling={cancelRegistration.isLoading}
          />
        );
      case 2:
        // Once registered, the registration id is in the URL. Without the
        // register response in memory (refresh, return visit, or back from a
        // failed payment), load the pending registration from the API.
        if (params.currentlySchedulingId && !registerData.data) {
          return (
            <PendingEnrollmentPayment
              onCancel={onCancelRegistration}
              isCanceling={cancelRegistration.isLoading}
            />
          );
        }
        // Going back to the review step would register the cart again, so
        // only offer it while nothing is registered yet.
        return (
          <PayFees
            register={registerData}
            onGoBack={registerData.data ? undefined : () => setStep(1)}
            onCancel={onCancelRegistration}
            isCanceling={cancelRegistration.isLoading}
          />
        );
      case 3:
        return params.paymentIntentId ? (
          <SignDocumentation
            paymentIntentId={params.paymentIntentId}
            onGoBack={() => setStep(2)}
          />
        ) : null;
      case 4:
        return params.paymentIntentId ? (
          <Complete paymentIntentId={params.paymentIntentId} />
        ) : null;
      default:
        return null;
    }
  };

  return <>{renderStep()}</>;
};

export default StepRenderer;
